import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon, TrashIcon, EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useNavigate } from 'react-router-dom';
import { disconnectAppSocket, getAppSocket } from '@/lib/wesSocket';
import { Tip } from '@/components/ui/tip';
import { esRechazado } from '../utils/budgetItems';

type AlertRecord = {
  id: string;
  type: 'warning' | 'info' | 'error';
  notifType?: string;
  message: string;
  priority: 'high' | 'medium' | 'low';
  notification?: boolean;
  notificationId?: number;
  relatedType?: string | null;
  relatedId?: string | null;
  data?: any;
};

type Client = {
  id: number;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  type: 'individual' | 'company';
};

type Vehicle = {
  id: number;
  brand: string;
  model: string;
  year: number;
  plate?: string;
};

type BudgetItem = {
  id?: number;
  description?: string;
  name?: string;
  qty?: number;
  quantity?: number;
  totalPrice?: number;
  sellingPrice?: number;
  unitPrice?: number;
  optionCode?: string;
};

type Budget = {
  id: number;
  budgetNumber?: string;
  clientId: number;
  vehicleId?: number;
  status: string;
  validUntil: string;
  description?: string;
  createdAt: string;
  paymentMethod?: string;
  selectedOption?: string;
  items?: BudgetItem[];
};

type AlertResponse = {
  alerts: AlertRecord[];
};

const typeConfig = {
  warning: {
    icon: ExclamationTriangleIcon,
    label: 'Advertencia',
    className: 'text-amber-600 bg-amber-50 dark:text-amber-200 dark:bg-amber-500/10'
  },
  info: {
    icon: InformationCircleIcon,
    label: 'Información',
    className: 'text-blue-600 bg-blue-50 dark:text-blue-200 dark:bg-blue-500/10'
  },
  error: {
    icon: XCircleIcon,
    label: 'Crítica',
    className: 'text-red-600 bg-red-50 dark:text-red-200 dark:bg-red-500/10'
  }
};

const priorityStyles: Record<AlertRecord['priority'], string> = {
  high: 'bg-red-500/10 text-red-600 dark:text-red-200 dark:bg-red-500/20',
  medium: 'bg-amber-500/10 text-amber-600 dark:text-amber-200 dark:bg-amber-500/20',
  low: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-200 dark:bg-emerald-500/20'
};

const Alerts: React.FC = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<AlertRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewBudget, setViewBudget] = useState<Budget | null>(null);
  const [viewClient, setViewClient] = useState<Client | null>(null);
  const [viewVehicle, setViewVehicle] = useState<Vehicle | null>(null);
  const [dismissedAlertIds, setDismissedAlertIds] = useState<string[]>([]);
  const dismissedAlertsKey = 'dismissed-system-alerts';
  const refreshTimeoutRef = useRef<number | null>(null);
  const fetchAlertsRef = useRef<null | ((options?: { silent?: boolean; dismissedIds?: string[] }) => void)>(null);

  const fetchAlerts = async (options?: { silent?: boolean; dismissedIds?: string[] }) => {
    const silent = Boolean(options?.silent);
    const dismissedIds = options?.dismissedIds || dismissedAlertIds;
    try {
      if (!silent) setLoading(true);
      const { data } = await axios.get<AlertResponse>('/api/dashboard/alerts');
      const nextAlerts = data?.alerts || [];
      const filteredAlerts = nextAlerts.filter((alert) => !dismissedIds.includes(alert.id));
      setAlerts(filteredAlerts);
    } catch (error: any) {
      console.error('Error al obtener alertas:', error);
      if (!silent) toast.error(error?.response?.data?.error || 'No se pudieron cargar las alertas');
    } finally {
      if (!silent) setLoading(false);
    }
  };
  fetchAlertsRef.current = fetchAlerts;

  const getAlertLink = (alert: AlertRecord): string | null => {
    const t = alert.notifType || alert.relatedType || '';
    // La pagina de Observaciones se retiro (reemplazada por el checklist);
    // las incidencias se atienden desde el panel de la OT en el Taller
    if (t === 'vehicle_observation') return '/taller';
    if (t === 'wes_action' || t === 'task') return '/taller';
    if (t === 'stock_negative' || t === 'stock_alerts') return '/products';
    if (t === 'budget_accepted' || alert.relatedType === 'budget') return null; // handled by handleViewBudget
    return null;
  };

  const handleViewBudget = async (alert: AlertRecord) => {
    try {
      const data = alert.data ? JSON.parse(alert.data) : null;
      if (!data || !data.budgetId) {
        toast.error('No se encontró información del presupuesto');
        return;
      }

      // Fetch budget data
      const budgetResponse = await axios.get(`/api/budgets/${data.budgetId}`);
      const budgetData = budgetResponse.data;

      console.log('Budget data:', budgetData);
      console.log('Client ID from budget:', budgetData.clientId);

      // Fetch client data
      const clientResponse = await axios.get(`/api/clients/${budgetData.clientId}`);
      // La API devuelve {client: {...}}, así que extraemos el cliente
      const client = clientResponse.data.client || clientResponse.data;

      console.log('Client response data:', clientResponse.data);
      console.log('Client data:', client);
      console.log('Client type:', client.type);
      console.log('Client firstName:', client.firstName);
      console.log('Client lastName:', client.lastName);
      console.log('Client companyName:', client.companyName);

      // Fetch vehicle data if exists
      let vehicle = null;
      if (budgetData.vehicleId) {
        const vehicleResponse = await axios.get(`/api/vehicles/${budgetData.vehicleId}`);
        vehicle = vehicleResponse.data;
      }

      setViewBudget(budgetData);
      setViewClient(client);
      setViewVehicle(vehicle);
      setShowViewModal(true);
    } catch (error: any) {
      console.error('Error al ver presupuesto:', error);
      toast.error('Error al abrir el presupuesto');
    }
  };

  const handleDeleteNotification = async (notificationId: number) => {
    try {
      await axios.delete(`/api/notifications/${notificationId}`);
      toast.success('Notificación eliminada');
      setAlerts((prev) => prev.filter((alert) => alert.notificationId !== notificationId));
      fetchAlerts();
    } catch (error: any) {
      console.error('Error al eliminar notificación:', error);
      toast.error('Error al eliminar la notificación');
    }
  };

  const handleDismissAlert = (alertId: string) => {
    const nextIds = dismissedAlertIds.includes(alertId)
      ? dismissedAlertIds
      : [...dismissedAlertIds, alertId];
    setDismissedAlertIds(nextIds);
    localStorage.setItem(dismissedAlertsKey, JSON.stringify(nextIds));
    setAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
    toast.success('Alerta descartada');
  };

  const handleContactWhatsApp = async (alert: AlertRecord) => {
    try {
      // Extraer información del presupuesto de los datos de la alerta
      const data = alert.data ? JSON.parse(alert.data) : null;
      if (!data || !data.budgetId) {
        toast.error('No se encontró información del presupuesto');
        return;
      }

      // Obtener datos del presupuesto y cliente
      const budgetResponse = await axios.get(`/api/budgets/${data.budgetId}`);
      const budget = budgetResponse.data;

      if (!budget.clientId) {
        toast.error('No se encontró el cliente asociado');
        return;
      }

      const clientResponse = await axios.get(`/api/clients/${budget.clientId}`);
      const client = clientResponse.data;

      if (!client.phone) {
        toast.error('El cliente no tiene número de teléfono registrado');
        return;
      }

      // Formatear el número y abrir WhatsApp
      const phone = client.phone.toString().replace(/\D/g, '');
      const message = encodeURIComponent(
        `Hola ${client.firstName || ''} ${client.lastName || ''}! Vimos que aceptaste el presupuesto ${budget.budgetNumber || data.budgetNumber}. ¿Te gustaría coordinar los detalles?`
      );

      window.open(`https://wa.me/549${phone}?text=${message}`, '_blank');
      toast.success('Abriendo WhatsApp...');
    } catch (error: any) {
      console.error('Error al contactar por WhatsApp:', error);
      toast.error('Error al abrir WhatsApp');
    }
  };

  useEffect(() => {
    let initialDismissed: string[] = [];
    const stored = localStorage.getItem(dismissedAlertsKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          initialDismissed = parsed;
          setDismissedAlertIds(parsed);
        }
      } catch (error) {
        console.warn('No se pudieron cargar alertas descartadas:', error);
      }
    }

    fetchAlerts({ dismissedIds: initialDismissed });

    const socket = getAppSocket();
    const scheduleSilentRefresh = () => {
      if (refreshTimeoutRef.current) window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = window.setTimeout(() => {
        fetchAlertsRef.current?.({ silent: true });
      }, 400);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
      disconnectAppSocket();
    };
  }, []);

  const getClientName = (client: Client | null): string => {
    console.log('getClientName called with:', client);
    if (!client) {
      console.log('Client is null, returning "Sin cliente"');
      return 'Sin cliente';
    }

    console.log('Client type:', client.type);
    console.log('Company name:', client.companyName);
    console.log('First name:', client.firstName);
    console.log('Last name:', client.lastName);

    if (client.type === 'company' && client.companyName) {
      console.log('Returning company name:', client.companyName);
      return client.companyName;
    }

    const fullName = `${client.firstName || ''} ${client.lastName || ''}`.trim();
    console.log('Returning full name:', fullName);
    return fullName || 'Sin nombre';
  };

  const getVehicleInfo = (vehicle: Vehicle | null): string => {
    if (!vehicle) return 'Sin vehículo';
    return `${vehicle.brand} ${vehicle.model} ${vehicle.year}${vehicle.plate ? ` - ${vehicle.plate}` : ''}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'approved': return 'Aprobado';
      case 'rejected': return 'Rechazado';
      case 'expired': return 'Expirado';
      default: return status;
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Alertas del sistema</h1>
      </div>

      <div className="rounded-lg border bg-card">
        {loading ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
            Cargando alertas...
          </div>
        ) : alerts.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center text-center text-muted-foreground">
            <ExclamationTriangleIcon className="mb-2 h-8 w-8 text-muted-foreground/60" />
            <p className="text-sm font-medium">No hay alertas activas</p>
            <p className="text-xs">El sistema notificará automáticamente cuando detecte un evento relevante.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Alerta</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Prioridad</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map((alert) => {
                const config = typeConfig[alert.type];
                const Icon = config.icon;
                const isBudgetAccepted = alert.id.includes('notification-') && alert.message.includes('presupuesto');

                return (
                  <TableRow key={alert.id} className="h-12">
                    <TableCell className="py-2">
                      <div className="flex items-center gap-3">
                        <span className={`rounded-md p-1.5 ${config.className}`}>
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{alert.message}</span>
                          <span className="text-xs text-muted-foreground">
                            {alert.id.replace(/-/g, ' ')}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge variant="secondary" className={config.className}>
                        {config.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge className={priorityStyles[alert.priority]}>
                        {alert.priority === 'high' && 'Alta'}
                        {alert.priority === 'medium' && 'Media'}
                        {alert.priority === 'low' && 'Baja'}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isBudgetAccepted && (
                          <>
                            <Tip label="Ver presupuesto">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleViewBudget(alert)}
                                className="size-8"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                            <Tip label="WhatsApp">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleContactWhatsApp(alert)}
                                className="size-8"
                              >
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                                </svg>
                              </Button>
                            </Tip>
                          </>
                        )}
                        {(() => {
                          const link = getAlertLink(alert);
                          return link ? (
                            <Tip label="Ver detalle">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => navigate(link)}
                                className="size-8"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                          ) : null;
                        })()}
                        {alert.notification && alert.notificationId ? (
                          <Tip label="Eliminar">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleDeleteNotification(alert.notificationId!)}
                              className="size-8"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </Tip>
                        ) : (
                          <Tip label="Descartar">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleDismissAlert(alert.id)}
                              className="size-8"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </Tip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Modal de visualización de presupuesto */}
      {showViewModal && viewBudget && viewClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-card">
            <div className="flex justify-between items-center p-6 border-b border-border">
              <h3 className="text-xl font-semibold text-foreground">
                Presupuesto {viewBudget.budgetNumber || `P-${viewBudget.id.toString().slice(-6)}`}
              </h3>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewBudget(null);
                  setViewClient(null);
                  setViewVehicle(null);
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Información General */}
              <div>
                <h4 className="text-lg font-semibold mb-4 text-foreground">Información General</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Cliente</span>
                    <p className="text-foreground">{getClientName(viewClient)}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Vehículo</span>
                    <p className="text-foreground">
                      {viewVehicle ? getVehicleInfo(viewVehicle) : 'Sin vehículo'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Estado</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      viewBudget.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      viewBudget.status === 'approved' ? 'bg-green-100 text-green-800' :
                      viewBudget.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      viewBudget.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {getStatusText(viewBudget.status)}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Válido hasta</span>
                    <p className="text-foreground">{formatDate(viewBudget.validUntil)}</p>
                  </div>
                </div>
              </div>

              {/* Detalles */}
              <div>
                <h4 className="text-lg font-semibold mb-4 text-foreground">Detalles</h4>
                <div className="space-y-3">
                  {viewBudget.description && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Observaciones</span>
                      <p className="text-foreground">{viewBudget.description}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Creado</span>
                    <p className="text-foreground">{formatDate(viewBudget.createdAt)}</p>
                  </div>
                  {viewBudget.paymentMethod && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Medio de pago seleccionado</span>
                      <p className="text-foreground">
                        <span className="font-semibold text-blue-600">{
                          viewBudget.paymentMethod === 'tc6' ? 'TC 6' :
                          viewBudget.paymentMethod === 'tc3' ? 'TC 3P' :
                          viewBudget.paymentMethod === 'contado' ? 'Contado' :
                          viewBudget.paymentMethod
                        }</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Productos y Servicios */}
              <div>
                <h4 className="text-lg font-semibold mb-4 text-foreground">Productos y Servicios</h4>
                <div className="space-y-2">
                  {(() => {
                    const items: BudgetItem[] = Array.isArray(viewBudget.items) ? viewBudget.items : [];
                    const hasOptions = items.some((it) => !!it.optionCode);
                    let toShow: BudgetItem[] = items;
                    if (hasOptions && viewBudget.selectedOption) {
                      const base = items.filter((it) => !it.optionCode);
                      const chosen = items.filter((it) => String(it.optionCode) === String(viewBudget.selectedOption));
                      toShow = [...base, ...chosen];
                    }
                    return toShow && toShow.length > 0 ? (
                      toShow.map((item, index) => {
                        const rechazado = esRechazado(item);
                        return (
                        <div key={index} className="flex justify-between items-center py-2 border-b border-border">
                          <div className={rechazado ? 'text-muted-foreground line-through' : 'text-foreground'}>
                            <span>{item.description || item.name}</span>
                            <span className="ml-2 text-muted-foreground">x{item.qty || item.quantity || 1}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {rechazado && (
                              <span className="rounded-full border border-destructive/40 px-2 py-0.5 text-[11px] font-medium text-destructive">Rechazado</span>
                            )}
                            <span className={`font-medium ${rechazado ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                              {formatCurrency((item.totalPrice) ?? ((item.sellingPrice || item.unitPrice || 0) * (item.qty || item.quantity || 1)))}
                            </span>
                          </div>
                        </div>
                        );
                      })
                    ) : (
                      <p className="text-muted-foreground">No hay items en este presupuesto</p>
                    );
                  })()}
                </div>
              </div>

              {/* Total */}
              {viewBudget.paymentMethod && (
                <div className="mt-6 border-t border-border pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-foreground">Total</span>
                    <span className="text-2xl font-bold text-foreground">
                      {(() => {
                        // Calcular total base de los items
                        const items: BudgetItem[] = Array.isArray(viewBudget.items) ? viewBudget.items : [];
                        const hasOptions = items.some((it) => !!it.optionCode);
                        let toShow: BudgetItem[] = items;
                        if (hasOptions && viewBudget.selectedOption) {
                          const base = items.filter((it) => !it.optionCode);
                          const chosen = items.filter((it) => String(it.optionCode) === String(viewBudget.selectedOption));
                          toShow = [...base, ...chosen];
                        }

                        const totalAmount = toShow.filter((it) => !esRechazado(it)).reduce((sum, item) => {
                          const itemTotal = item.totalPrice ?? ((item.sellingPrice || item.unitPrice || 0) * (item.qty || item.quantity || 1));
                          return sum + itemTotal;
                        }, 0);

                        // Aplicar recargo según el medio de pago
                        const paymentMethod = viewBudget.paymentMethod?.toLowerCase();
                        if (paymentMethod === 'contado') return formatCurrency(totalAmount);
                        if (paymentMethod === 'tc3' || paymentMethod === 'tc 3p') return formatCurrency(Math.round(totalAmount * 1.15));
                        if (paymentMethod === 'tc6' || paymentMethod === 'tc 6p') return formatCurrency(Math.round(totalAmount * 1.25));
                        return formatCurrency(totalAmount);
                      })()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Alerts;
